import { create } from "zustand";
import { getContentLocales } from "@/lib/api/properties";
import type { ContentLocale } from "@/types";

interface LocaleState {
  locales: ContentLocale[];
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  /** Locale chosen from listing “Add language” — translation sidecars use this. */
  preferredTranslationLocale: string | null;
  fetchLocales: () => Promise<void>;
  setPreferredTranslationLocale: (code: string | null) => void;
}

// Module-level so a fetch in flight isn't duplicated by a second mount.
let pendingFetch: Promise<void> | null = null;

/** Active content locales, fetched once and cached — see useActiveLanguages for the picker hook that uses this. */
export const useContentLocaleStore = create<LocaleState>((set, get) => ({
  locales: [],
  isLoaded: false,
  isLoading: false,
  error: null,
  preferredTranslationLocale: null,
  setPreferredTranslationLocale: (code) => set({ preferredTranslationLocale: code }),
  fetchLocales: async () => {
    if (get().isLoaded) return;
    if (pendingFetch) return pendingFetch;

    set({ isLoading: true, error: null });
    pendingFetch = (async () => {
      try {
        const locales = await getContentLocales();
        set({ locales: [...locales].sort((a, b) => a.sortOrder - b.sortOrder), isLoaded: true });
      } catch (err) {
        set({ error: err instanceof Error ? err.message : String(err) });
      } finally {
        set({ isLoading: false });
        pendingFetch = null;
      }
    })();
    return pendingFetch;
  },
}));

/** Read outside React for mergeVariantsToWire. Call ensureActiveLanguagesLoaded() first from a save path — an empty-because-still-loading list would wrongly skip nulling a removed variant. */
export function getActiveLanguageCodes(): string[] {
  return useContentLocaleStore.getState().locales.map((l) => l.code);
}

/** Throws if the fetch failed (locales stayed empty) rather than letting a save continue and silently skip nulling a removed translation. */
export async function ensureActiveLanguagesLoaded(): Promise<void> {
  await useContentLocaleStore.getState().fetchLocales();
  if (!useContentLocaleStore.getState().isLoaded) {
    const err = useContentLocaleStore.getState().error;
    throw new Error(err ? `Could not load active languages: ${err}` : "Could not load active languages — check your connection and try again.");
  }
}
